import React, { useState } from 'react';
import * as Yup from "yup";
import { Formik, Form } from "formik";
import { useAuth } from "../../providers/AuthProvider";
import styles from "../../styles/common/UserSettings.module.scss";

import FormInput from "../form-elements/FormInput";
import FormGroupErrorText from "../form-elements/FormGroupErrorText";
import Button from "../common/Button";

const TwoFactorSetupSchema = Yup.object().shape({
  otp: Yup.string()
    .required("Verification code is required")
    .matches(/^\d{6}$/, "Must be exactly 6 digits")
});

function TwoFactorSetupForm() {
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState<string | null>(null);
  const [secretKey, setSecretKey] = useState<string | null>(null);
  const [setupCompleted, setSetupCompleted] = useState(false);
  const [setupStarted, setSetupStarted] = useState(false);

  const { setup2FA, verify2FASetup, disable2FA, user } = useAuth();

  const startSetup = async () => {
    if (user?.two_factor_enabled) {
      return; // Skip if 2FA is already enabled
    }
    
    setSetupStarted(true);
    fetchSetupData();
  };

  const fetchSetupData = async () => {
    try {
      setError(null);
      setIsLoading(true);
      const { qrCodeUrl, secretKey } = await setup2FA();
      setQrCodeUrl(qrCodeUrl);
      setSecretKey(secretKey);
    } catch (err: any) {
      const errorMessage = err.message || "Failed to get 2FA setup information. Please try again.";
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifySubmit = async (
    values: { otp: string },
    { setSubmitting, resetForm, setFieldError }: { setSubmitting: (isSubmitting: boolean) => void, resetForm: () => void, setFieldError: (field: string, message: string) => void }
  ) => {
    setError(null);
    setSuccess(null);
    setIsLoading(true);

    try {
      const result = await verify2FASetup(values.otp);
      if (result) {
        setSetupCompleted(true);
        resetForm();
      } else {
        setFieldError('otp', "Failed to verify the code. Please try again.");
      }
    } catch (err: any) {
      let errorMessage = "Verification failed. Please try again.";
      if (err.message) {
        errorMessage = err.message;
      }
      setFieldError('otp', errorMessage);
    } finally {
      setIsLoading(false);
      setSubmitting(false);
    }
  };

  const handleDisable2FA = async () => {
    setError(null);
    setSuccess(null);
    setIsLoading(true);

    try {
      const result = await disable2FA();
      if (result) {
        setSuccess("Two-Factor Authentication has been disabled.");
        setQrCodeUrl(null);
        setSecretKey(null);
        setSetupCompleted(false);
      } else {
        setError("Failed to disable 2FA. Please try again.");
      }
    } catch (err: any) {
      let errorMessage = "Failed to disable 2FA. Please try again.";
      if (err.message) {
        errorMessage = err.message;
      }
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // If 2FA is already enabled, show the disable option
  if (user?.two_factor_enabled || setupCompleted) {
    return (
      <div className={styles.twoFactorSetup}>
        <div className={styles.statusEnabled}>
          <span className={styles.statusIcon}>✓</span>
          <span className={styles.statusText}>Two-Factor Authentication is enabled</span>
        </div>
        {error && <div className={styles.errorMessage}>{error}</div>}
        <div className={styles.buttonContainer} style={{ marginTop: '1.5rem' }}>
          <Button
            btnColor="white-outline"
            btnSize="md"
            onClick={handleDisable2FA}
            isLoading={isLoading}
            isLoadingText="Disabling"
            text="Disable Two-Factor Authentication"
          />
        </div>
      </div>
    );
  }

  // Setup not complete, show the setup form or setup button
  return (
    <div className={styles.twoFactorSetup}>
      {!setupStarted && !user?.two_factor_enabled && (
        <div className={styles.setupIntro}>
          <p>Two-factor authentication provides additional security by requiring a verification code in addition to your password.</p>
          <div className={styles.setupActions}>
            <Button
              text="Set up Two-Factor Authentication"
              onClick={startSetup}
              btnSize="lg"
              btnColor="white"
            />
            <p className={styles.setupNote}>
              You'll need an authenticator app like Google Authenticator or Authy
            </p>
          </div>
        </div>
      )}
      
      {setupStarted && (
        <>
          {isLoading && !qrCodeUrl && (
            <div className={styles.loadingContainer}>
              <p>Loading setup information...</p>
            </div>
          )}
          
          {error && !qrCodeUrl && !isLoading && (
            <div className={styles.errorMessage}>
              {error}
              <div className={styles.buttonContainer} style={{ marginTop: '1rem' }}>
                <Button
                  text="Try Again"
                  onClick={fetchSetupData}
                  btnColor="white"
                  btnSize="md"
                />
              </div>
            </div>
          )}

          {qrCodeUrl && (
            <div className={styles.qrCodeContainer}>
              <p className={styles.stepTitle}>1. Scan this QR code with your authenticator app</p>
              <p>Use an app like Google Authenticator or Authy to scan this code:</p>
              <img src={qrCodeUrl} alt="QR Code for 2FA setup" className={styles.qrCode} />
              
              <div className={styles.secretKeyContainer}>
                <p>Or manually enter this key in your authenticator app:</p>
                <code className={styles.secretKey}>{secretKey}</code>
              </div>
            </div>
          )}
        </>
      )}

      {setupStarted && qrCodeUrl && (
        <Formik
          initialValues={{ otp: "" }}
          validateOnMount={true}
          validateOnChange={true}
          validationSchema={TwoFactorSetupSchema}
          onSubmit={handleVerifySubmit}
        >
          {({ isSubmitting, errors, isValid, touched, dirty }) => (
            <Form className="c-form" noValidate>
              <div className={styles.verifySection}>
                <p className={styles.verifyTitle}>
                  2. Verify setup
                </p>
                
                <p className={styles.formInstructions}>
                  Enter the 6-digit code from your authenticator app
                </p>

                <FormInput
                  extraClassName="boxed"
                  formGroupStyle="block-line"
                  labelText="Authentication Code"
                  type="text"
                  name="otp"
                  placeholder="Enter 6-digit code"
                  autoComplete="one-time-code"
                  hideError
                  maxLength={6}
                />

                {(errors.otp && touched.otp) || error ? (
                  <div className={styles.verifyErrorContainer}>
                    <FormGroupErrorText text={errors.otp && touched.otp ? errors.otp : error || ''}/>
                  </div>
                ) : null}
                
                {success && <div className={styles.successMessage}>{success}</div>}
              </div>

              <div className={styles.buttonContainer}>
                <Button
                  type="submit"
                  btnSize="lg"
                  btnColor="white"
                  disabled={!isValid || isSubmitting || !dirty}
                  isLoading={isLoading}
                  isLoadingText="Verifying"
                  text="Enable Two-Factor Authentication"
                />
              </div>
            </Form>
          )}
        </Formik>
      )}
    </div>
  );
}

export default TwoFactorSetupForm;